import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { ErrorState, Skeleton, useToast } from '@/components/ui';
import { triggerCatalogSync } from '@/api/console';
import { AppDetailHeader } from './AppDetail/AppDetailHeader';
import { AppDetailOverviewTab } from './AppDetail/AppDetailOverviewTab';
import { AppDetailRolesTab } from './AppDetail/AppDetailRolesTab';
import { AppDetailAccountsSection } from './AppDetail/AppDetailAccountsSection';
import { AppDetailOrganizationsTab } from './AppDetail/AppDetailOrganizationsTab';
import { AppDetailConnectionTab } from './AppDetail/AppDetailConnectionTab';
import { AppDetailConfigTab } from './AppDetail/AppDetailConfigTab';
import { AppDetailAuditTab } from './AppDetail/AppDetailAuditTab';
import { AppApprovalActions } from './AppDetail/AppApprovalActions';
import { AppApprovalCheckPanel } from './AppDetail/AppApprovalCheckPanel';
import { useAppDetailPage } from './AppDetail/useAppDetailPage';
import { SyncUsersModal } from './Apps/SyncUsersModal';
import { ImportUsersModal } from './Apps/ImportUsersModal';
import { SyncOrganizationsModal } from './Apps/SyncOrganizationsModal';

/**
 * EAA-1817 (frames 47-51): `/console/apps/:code` — the app-detail page the
 * frame coverage table in `docs/SRS-Portal-Apps-RoleMap-Frontend.md` marked
 * "Not shipped". Thin orchestrator: gating, tab routing, and composing the
 * seven tabs (frame 53/54 "Tài khoản"/"Tổ chức" added 2026-08-15; EAA-1823
 * added "Cấu hình") — each tab
 * file carries its own data needs beyond what `useAppDetailPage` centralises
 * (the registry row lookup, role-conversion derivation, the provisioning
 * outcomes feed, and the audit feed every tab can read from).
 */
export function AppDetailPage() {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const vm = useAppDetailPage();
    // EAA-1861: 'sync' | 'import' | null rather than two booleans — the two
    // dialogs are mutually exclusive entry points to the same underlying
    // action (see SyncUsersModal's `onSwitchToImport` escape hatch).
    const [userSyncDialog, setUserSyncDialog] = useState<'sync' | 'import' | null>(null);
    // EAA-1878: a single mutually-exclusive-with-nothing action, unlike the
    // sync/import pair above — a plain boolean is enough (PLAN-lms-org-school-
    // sync.md §3 WP-4 point 4).
    const [orgSyncDialogOpen, setOrgSyncDialogOpen] = useState(false);

    // EAA-1817: `POST /role-map/sync` has no per-app parameter — it syncs
    // every app's catalog in one pass (see `RoleMapPage`'s own sync button).
    // This page's "Đồng bộ danh mục" action necessarily triggers the same
    // ecosystem-wide sync, not a scoped one; there is no narrower endpoint.
    const syncMutation = useMutation({
        mutationFn: () => triggerCatalogSync(config.apiBase, accessToken as string),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            queryClient.invalidateQueries({ queryKey: ['admin-app-roles', vm.code] });
            toast.success(t('console.appDetail.syncSuccess'));
        },
        onError: () => toast.error(t('console.appDetail.syncError')),
    });

    // EAA-1817 (frame 53): the accounts tab's "Lấy người dùng từ LMS" affordance
    // opens the SAME dialog the header button does — a stable callback shared
    // by both call sites (rather than a second inline ternary) so there is one
    // definition of "when is this action offered" to keep in sync.
    const canOpenSyncUsers = vm.canSyncUsers && !vm.app?.is_internal;
    const handleOpenSyncUsers = useCallback(() => setUserSyncDialog('sync'), []);

    // EAA-1817 (fail-closed): unmount entirely without view capability.
    if (!vm.canView) {
        return null;
    }

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit app-detail-page">
            {vm.appsQuery.isLoading && (
                <div role="status" aria-live="polite">
                    <span className="portal-sr-only">{t('common.loading')}</span>
                    <Skeleton height="4rem" />
                </div>
            )}

            {vm.appsQuery.isError && (
                <ErrorState
                    description={t('console.appDetail.loadError')}
                    onRetry={() => vm.appsQuery.refetch()}
                    retrying={vm.appsQuery.isFetching}
                />
            )}

            {vm.appsQuery.isSuccess && !vm.app && (
                <ErrorState description={t('console.appDetail.notFound', { code: vm.code })} />
            )}

            {vm.app && (
                <>
                    {/* EAA-1817: `AppDetailHeader` and the tab panel below are direct
                        children of `.portal-console-page--fit` on purpose — the fit
                        rule targets `> *:not(.fit-grow)`, so a wrapping div here would
                        put `.fit-grow` two levels down and break the `min-height: 0`
                        chain the shell-fit contract depends on. */}
                    <AppDetailHeader
                        code={vm.code}
                        app={vm.app}
                        tab={vm.tab}
                        onSelectTab={vm.setTab}
                        // EAA-1884: `!vm.app.is_internal` is ANDed onto all three sync
                        // handlers below, on top of each action's own permission check.
                        // An internal app (ACH, the only one today) shares this exact
                        // backend deployment rather than being a separate system with its
                        // own roles/users/orgs to reconcile — so none of "Đồng bộ danh
                        // mục" / "Đồng bộ người dùng" / "Đồng bộ tổ chức" has anything
                        // external to sync against. Offering them would at best be a
                        // no-op the UI can't explain, at worst (a naive org-sync) risk
                        // writing back over this same database's real data. Same
                        // render-site-combination style as the `supports_org_sync` gate
                        // this shipped alongside (bd77d3e) — permission and capability
                        // stay separate `can(...)`/app-data checks, ANDed here rather than
                        // folded into `useAppDetailPage`.
                        onSync={
                            vm.canSync && !vm.app.is_internal
                                ? () => syncMutation.mutate()
                                : undefined
                        }
                        canEdit={vm.canManage}
                        onSyncUsers={canOpenSyncUsers ? handleOpenSyncUsers : undefined}
                        // EAA-1878: `canSyncOrganizations` is the general permission
                        // (can this operator ever trigger an org sync); `supports_org_sync`
                        // is the per-app capability (does THIS app's backend even expose
                        // `/management/organizations`). Neither alone is sufficient — same
                        // "absent, not disabled" reasoning `AppsTable.tsx` already applies to
                        // `supports_role_management`: ACH has no org-sync receiver by design
                        // (its Organization<->Tenant link is wired through a separate,
                        // pre-existing mechanism), so offering this button there would only
                        // ever end in a validation error the UI has no good way to explain.
                        // Kept as a render-site combination rather than folded into
                        // `useAppDetailPage`'s `canSyncOrganizations` — that value and its
                        // siblings (`canSync`, `canSyncUsers`) are plain `can(...)` permission
                        // checks with no app data mixed in, and `AppsTable.tsx` keeps the same
                        // separation (its `canManage` permission gate and its
                        // `supports_role_management` capability gate live at two different
                        // layers too).
                        // EAA-1884: `!vm.app.is_internal` is ANDed on too, defense in depth —
                        // in practice an internal app should never have `supports_org_sync`
                        // true either, but checking both layers costs nothing and matches
                        // this same "fail-closed on both layers" philosophy.
                        onSyncOrganizations={
                            vm.canSyncOrganizations &&
                            vm.app.supports_org_sync &&
                            !vm.app.is_internal
                                ? () => setOrgSyncDialogOpen(true)
                                : undefined
                        }
                        // EAA-1709: check → approve, for an app that is not enabled yet.
                        leadingActions={
                            vm.app.enabled ? undefined : (
                                <AppApprovalActions app={vm.app} approval={vm.approval} />
                            )
                        }
                    />

                    {/* EAA-1709: natural height, a direct child for the same fit-rule
                        reason as the header above; renders only after a check. The
                        connection tab shows the same report in its own check card. */}
                    {!vm.app.enabled && vm.tab !== 'connection' && (
                        <AppApprovalCheckPanel approval={vm.approval} />
                    )}

                    <div className="app-detail-tab-panel fit-grow">
                        {vm.tab === 'overview' && <AppDetailOverviewTab vm={vm} />}
                        {vm.tab === 'roles' && <AppDetailRolesTab vm={vm} />}
                        {/* EAA-2068: "Tài khoản" (the shared accounts board locked to
                            this app) + "Đồng bộ" (the provisioning view this tab used
                            to be) — see `AppDetailAccountsSection`. */}
                        {vm.tab === 'accounts' && (
                            <AppDetailAccountsSection
                                vm={vm}
                                onSyncUsers={canOpenSyncUsers ? handleOpenSyncUsers : undefined}
                            />
                        )}
                        {vm.tab === 'organizations' && <AppDetailOrganizationsTab vm={vm} />}
                        {vm.tab === 'connection' && <AppDetailConnectionTab vm={vm} />}
                        {vm.tab === 'config' && <AppDetailConfigTab vm={vm} />}
                        {vm.tab === 'audit' && <AppDetailAuditTab vm={vm} />}
                    </div>

                    <SyncUsersModal
                        app={userSyncDialog === 'sync' ? vm.app : null}
                        onClose={() => setUserSyncDialog(null)}
                        onSwitchToImport={() => setUserSyncDialog('import')}
                    />
                    <ImportUsersModal
                        app={userSyncDialog === 'import' ? vm.app : null}
                        onClose={() => setUserSyncDialog(null)}
                    />
                    <SyncOrganizationsModal
                        app={orgSyncDialogOpen ? vm.app : null}
                        onClose={() => setOrgSyncDialogOpen(false)}
                        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['admin-apps'] })}
                    />
                </>
            )}
        </main>
    );
}

export default AppDetailPage;
